import { useState } from 'react'
import { MapPin, Building2, Heart } from 'lucide-react'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import { useTranslation } from 'react-i18next'

export interface Charity {
  id: string
  name: string
  registry: string
  address: string
  logo?: string
  category: 'zakat' | 'sadaqah' | 'orphans'
}

interface CharityCardProps {
  charity: Charity
  onDonate: (charity: Charity, amount?: number) => void
}

export default function CharityCard({ charity, onDonate }: CharityCardProps) {
  const { t } = useTranslation()
  const [quickAmount, setQuickAmount] = useState<string>('')

  const quickAmounts = [50, 100, 250, 500, 1000]

  const handleQuickDonate = (amount: number) => {
    onDonate(charity, amount)
  }

  const handleCustomDonate = () => {
    const amount = parseFloat(quickAmount)
    if (amount > 0) {
      onDonate(charity, amount)
    }
  }

  const categoryColors = {
    zakat: 'bg-yellow-100 text-yellow-800',
    sadaqah: 'bg-green-100 text-green-800',
    orphans: 'bg-blue-100 text-blue-800',
  }

  const categoryLabels = {
    zakat: t('categories.zakat', 'زكاة'),
    sadaqah: t('categories.sadaqah', 'صدقة'),
    orphans: t('categories.orphans', 'الأيتام'),
  }

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="flex items-start gap-4">
        {/* Logo */}
        <div className="w-20 h-20 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
          {charity.logo ? (
            <img src={charity.logo} alt={charity.name} className="w-full h-full object-contain rounded-lg" />
          ) : (
            <Heart className="w-10 h-10 text-primary-600" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">{charity.name}</h3>
            <span className={cn('px-2 py-1 rounded text-xs font-medium flex-shrink-0', categoryColors[charity.category])}>
              {categoryLabels[charity.category]}
            </span>
          </div>

          <div className="space-y-1 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Building2 className="w-4 h-4" />
              <span>{t('common.registry', 'السجل')}: {charity.registry}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <MapPin className="w-4 h-4" />
              <span>{charity.address}</span>
            </div>
          </div>

          {/* Quick Amount Buttons */}
          <div className="mb-3">
            <p className="text-xs text-gray-500 mb-2">{t('common.quickAmounts', 'مبالغ سريعة')}:</p>
            <div className="flex flex-wrap gap-2">
              {quickAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => handleQuickDonate(amount)}
                  className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 hover:bg-primary-100 dark:hover:bg-primary-900/30 hover:text-primary-600 dark:hover:text-primary-400 rounded transition-colors"
                >
                  {amount} {t('common.currency', 'ج.م')}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Amount Input */}
          <div className="flex gap-2">
            <input
              type="number"
              value={quickAmount}
              onChange={(e) => setQuickAmount(e.target.value)}
              placeholder={t('common.enterAmount', 'أدخل المبلغ')}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-200"
            />
            <Button
              variant="primary"
              size="sm"
              onClick={handleCustomDonate}
              disabled={!quickAmount || parseFloat(quickAmount) <= 0}
            >
              {t('common.donate', 'تبرع')}
            </Button>
          </div>
        </div>
      </div>
    </Card>
  )
}

